import {
  AgentsGroup,
  ChatSettingsModalState,
  ChatSettingsTab,
  ConnectorsGroup,
  SkillsGroup,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {assertNever} from 'util/Types';

export function getChatUrlPath(chatId: string): string {
  return `/chats/${chatId}`;
}

const CHAT_URL_PATH_PATTERN = /^\/chats\/(chtsess_[A-Za-z0-9]+)\/?$/;

/**
 * Inverse of `getChatUrlPath`: the chat API ID if `urlPath` is exactly the URL of an existing chat,
 * otherwise null. Other `/chats/...` pages (new chat, settings, agent homepages) deliberately do not
 * match, since only a chat can be opened in the sidepanel by ID.
 */
export function parseChatIdFromChatUrlPath(urlPath: string): string | null {
  return CHAT_URL_PATH_PATTERN.exec(urlPath)?.[1] ?? null;
}

/** Base path for starting a new chat, registered in AIRoutes as the literal `chats/new`. */
const NEW_CHAT_BASE = 'chats/new';

/**
 * Query parameter naming the catalog use case whose prompt the composer opens with. It is read by
 * `openDraftChatFromQueryString`; this is the single source of truth for the URL direction.
 *
 * The value is a use case id from the catalog, which is already a persistence key and so is stable
 * enough to sit in a link that outlives a deploy. The key is kebab-case, matching the `use-cases`
 * path segment.
 */
export const CHAT_USE_CASE_QUERY_PARAM = 'use-case';
/**
 * Standard UTM campaign key. The value is stored on use-case events as `campaign` so the same
 * property can be filled from a first-party param later without renaming the event field.
 */
export const UTM_CAMPAIGN_QUERY_PARAM = 'utm_campaign';

/**
 * Campaign id from a landing URL, or null when the param is missing or blank.
 *
 * Reads the UTM key; callers attach the result as `campaign` on the use-case events.
 */
export function getChatUseCaseCampaign(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const campaign = value.trim();
  return campaign === '' ? null : campaign;
}

/**
 * Drop the use-case query parameter from the current URL without triggering routing.
 *
 * An unreachable use-case link should not keep advertising it in the address bar.
 * `utm_campaign` and other query parameters are left in place.
 */
export function stripChatUseCaseQueryParamFromLocation(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CHAT_USE_CASE_QUERY_PARAM)) {
    return;
  }
  url.searchParams.delete(CHAT_USE_CASE_QUERY_PARAM);
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * URL that opens a new chat with a use case's prompt in the composer, for CTAs that hand the user a
 * starter prompt.
 */
export function newChatWithUseCaseUrl(chatUseCaseId: string): string {
  return `/${NEW_CHAT_BASE}?${CHAT_USE_CASE_QUERY_PARAM}=${encodeURIComponent(chatUseCaseId)}`;
}

/**
 * URL for a foundry's own detail view, rendered in the sidepanel's main content slot.
 */
export function agentFoundryPageUrl(agentFoundryId: string): string {
  return `/agent-foundries/${encodeURIComponent(agentFoundryId)}`;
}

/**
 * URL for an Agent's (Routine's) homepage, rendered in the sidepanel's main content slot.
 */
export function agentHomepageUrl(routineId: string): string {
  return `/chats/agents/${encodeURIComponent(routineId)}`;
}

/**
 * URL for the Agent Foundries list, rendered in the sidepanel's main content slot (the same slot the
 * chat occupies).
 */
export const AGENT_FOUNDRIES_LIST_URL = '/chats/agent-foundries';

/**
 * Base path for the chat settings modal. The route patterns in AIRoutes are declared as literals
 * (so they stay greppable and their precedence is easy to eyeball); this constant is the single
 * source of truth for the state -> URL direction below.
 */
const CHAT_SETTINGS_MODAL_BASE = 'chats/settings';

// Returns true if `pathname` matches the chat settings modal URL (base or any sub-route).
export function isChatSettingsModalPath(pathname: string): boolean {
  const base = `/${CHAT_SETTINGS_MODAL_BASE}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Stable URL segment for a tab. The enum values are internal identifiers; this exhaustive switch is
 * the single place that pins down the segment strings that appear in the address bar, so they stay
 * stable independently of the enum key/value casing.
 */
export function chatSettingsTabToUrlSegment(tab: ChatSettingsTab): string {
  switch (tab) {
    case ChatSettingsTab.GENERAL:
      return 'general';
    case ChatSettingsTab.SKILLS:
      return 'skills';
    case ChatSettingsTab.AGENTS:
      return 'agents';
    case ChatSettingsTab.CONNECTORS:
      return 'connectors';
    case ChatSettingsTab.USE_CASES:
      return 'use-cases';
    default:
      throw new Error(`Unexpected tab for chat settings URL segment, tab: ${assertNever(tab)}`);
  }
}

/**
 * The default group each list tab lands on.
 */
const DEFAULT_SKILLS_GROUP = SkillsGroup.ALL;
const DEFAULT_AGENTS_GROUP = AgentsGroup.ALL;
const DEFAULT_CONNECTORS_GROUP = ConnectorsGroup.AVAILABLE;

/**
 * Build the URL-derived modal state for a tab from the route inputs.
 */
export function buildChatSettingsModalState(
  tab: ChatSettingsTab,
  selectedItemId: string | null,
  previous?: ChatSettingsModalState | null
): ChatSettingsModalState {
  switch (tab) {
    case ChatSettingsTab.GENERAL:
    case ChatSettingsTab.USE_CASES:
      return {tab};
    case ChatSettingsTab.SKILLS:
      return {
        tab,
        selectedItemId,
        group: previous?.tab === ChatSettingsTab.SKILLS ? previous.group : DEFAULT_SKILLS_GROUP,
      };
    case ChatSettingsTab.AGENTS:
      return {
        tab,
        selectedItemId,
        group: previous?.tab === ChatSettingsTab.AGENTS ? previous.group : DEFAULT_AGENTS_GROUP,
      };
    case ChatSettingsTab.CONNECTORS:
      return {
        tab,
        selectedItemId,
        group: previous?.tab === ChatSettingsTab.CONNECTORS ? previous.group : DEFAULT_CONNECTORS_GROUP,
      };
    default:
      throw new Error(`Unexpected tab for ChatSettingsModalState, tab: ${assertNever(tab)}`);
  }
}

/**
 * Build the URL for the chat settings modal from its state. Includes a leading slash so the address
 * bar updates correctly when passed to `navigateToURL` (matching every other caller); the
 * RouteBundle patterns themselves are registered without a leading slash, per Backbone.
 */
export function chatSettingsModalUrl(
  tab: ChatSettingsTab,
  options: {selectedItemId?: string | null; createNew?: boolean} = {}
): string {
  const {selectedItemId, createNew = false} = options;
  let url = `/${CHAT_SETTINGS_MODAL_BASE}/${chatSettingsTabToUrlSegment(tab)}`;
  if (createNew) {
    return `${url}/new`;
  }
  if (selectedItemId != null) {
    url += `/${encodeURIComponent(selectedItemId)}`;
  }
  return url;
}
